import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';

// fancy 3D modal styling
// import './FancyLanguageModal.css';  // disabled temporarily due to HMR caching issue

const languages = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'he', name: 'עברית', flag: '🇮🇱' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

const LanguageModal = ({ onClose }) => {
  const { i18n, t } = useTranslation();
  const currentLang = i18n.language.split('-')[0];

  const handleLanguageSelect = (code) => {
    i18n.changeLanguage(code);
    onClose();
  };

  // always use the plain modal; fancy version still present but unreachable
  const fancy = false; // was previously true for demo
  if (!fancy) {
    // original, lightweight modal
    return (
      <div className="modal-backdrop" onClick={onClose}>
        <motion.div
          className="modal-content"
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 25 }}
          style={{
            maxWidth: 420,
            maxHeight: '85vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1.5rem',
            paddingBottom: '1rem',
            borderBottom: '1px solid var(--color-accent-dim)',
          }}>
            <h2 style={{ margin: 0, fontSize: '1.5em' }}>🌐 {t('lang_button', 'Language')}</h2>
            <button
              type="button"
              onClick={onClose}
              style={{
                border: "none",
                background: "transparent",
                color: "var(--color-text-primary)",
                fontSize: 28,
                cursor: "pointer",
                padding: 0,
                lineHeight: 1,
              }}
              aria-label={t('close_button', 'Close')}
            >
              ×
            </button>
          </div>

          <div style={{
            flex: 1,
            overflowY: 'auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: 12,
            padding: '4px',
          }}>
            {languages.map(lang => {
              const isActive = currentLang === lang.code;
              return (
                <motion.button
                  key={lang.code}
                  onClick={() => handleLanguageSelect(lang.code)}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                  style={{
                    background: isActive 
                      ? 'linear-gradient(135deg, var(--color-accent), var(--color-accent-bright))' 
                      : 'var(--color-surface)',
                    border: isActive 
                      ? '2px solid var(--color-accent-bright)' 
                      : '1px solid var(--color-accent-dim)',
                    borderRadius: 'var(--border-radius-medium)',
                    padding: '16px 12px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: isActive ? 'var(--shadow-md)' : 'none',
                  }}
                >
                  <span style={{ fontSize: 32, lineHeight: 1 }}>{lang.flag}</span>
                  <span style={{
                    fontSize: 13,
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? 'var(--color-background)' : 'var(--color-text-primary)',
                    textAlign: 'center',
                    lineHeight: 1.2,
                  }}>
                    {lang.name}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>
    );
  }

  // fancy modal version with 3D card pile and CSS from FancyLanguageModal.css
  return (
    <div className="fm-overlay" onClick={onClose}>
      <motion.div
        className="fm-dialog"
        onClick={e => e.stopPropagation()}
        initial={{ rotateX: -90, opacity: 0 }}
        animate={{ rotateX: 0, opacity: 1 }}
        exit={{ rotateX: -90, opacity: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        <div className="fm-card-pile">
          <div className="fm-card" />
          <div className="fm-card" />
          <div className="fm-card" />
        </div>

        <div className="fm-header">
          <h2 style={{ margin: 0, fontSize: '1.5em' }}>🌐 {t('lang_button', 'Language')}</h2>
          <button type="button" onClick={onClose} className="fm-close" aria-label={t('close_button', 'Close')}>
            ×
          </button>
        </div>

        <div className="fm-content">
          {languages.map(lang => {
            const isActive = currentLang === lang.code;
            return (
              <motion.div
                key={lang.code}
                className={`fm-lang-button${isActive ? ' active' : ''}`}
                onClick={() => handleLanguageSelect(lang.code)}
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.95 }}
              >
                <span style={{ fontSize: 32, lineHeight: 1 }}>{lang.flag}</span>
                <span style={{
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? 'var(--color-background)' : 'var(--color-text-primary)',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}>
                  {lang.name}
                </span>
              </motion.div>
            );
          })}
        </div>
      </motion.div>
    </div>
  );
};

export default LanguageModal;
